/**
 * WareBee Digital Twin - Warehouse KPI Model
 * Master schema and category registry for the Decision-Support KPI Model
 */

import {
  ActivityEventProcessType,
  MappingSchema,
} from '@warebee/shared/import-converter';
import { t } from 'i18next';
import { TwTheme } from '../../../Tw';
import {
  getBestContrastColor,
  QualitativePaletteId,
  SequentialPaletteId,
} from '../../common/color.helper';
import { FormattedValueWithUnit } from '../../common/formatHelper';
import { ActualityAggregatedHqExtendedStatsDataColumn } from '../../feed/store/actuality.kpi.types';
import { HelpIndex, HelpNavTo } from '../../helpContext/help.default';

/**  Core KPI definition  */
export interface WarehouseKPI {
  optional?: any;
  id: ActualityAggregatedHqExtendedStatsDataColumn;
  title: string; // human-readable
  legend?: string; // chart legend title
  description?: string; // optional
  helpNavTo?: HelpNavTo<HelpIndex>;
  category: KPIGroup; // e.g. 'labour'
  subcategory?: string; // e.g. 'labour'
  unit?: '%' | 'units/hr' | 'orders/day' | 'min' | '$' | 'turns/yr' | string; // free text if not listed
  query?: string; // SQL Query
  formula?: string; // human readable formula
  tags?: string[]; // e.g. ['iso-22400','labor']
  getFormatted: (v: number) => FormattedValueWithUnit;

  /** Optional color for visual representation (e.g., chart colors, UI indicators) */
  color?: [string, string];

  // Enhanced benchmark structure
  benchmark?: EnhancedBenchmark;

  trendWindow?: string; // ISO-8601 duration, e.g. 'P28D'
  forecastHorizon?: string; // ISO-8601, e.g. 'P7D'
  drillDimensions?: string[]; // ['aisle','shift','client_id']
  alertTopic?: string; // Alert topic

  // Schema references
  schemaReferences?: SchemaReferences; // Reference to schema fields used in formula

  /**
   * The warehouse process this KPI is most closely associated with (if any).
   * Should match a value from ActivityEventProcessType.
   */
  processType?: ActivityEventProcessType | ActivityEventProcessType[];

  /** Relationships to other KPIs */
  relationships?: KPIRelationship[];

  /** Recommended visualization settings */
  recommendedVisualization?: RecommendedVisualization;

  /** Whether this is a primary/default KPI that should be highlighted in dashboards */
  isPrimary?: boolean;
  isDefault?: boolean;

  /** Dynamic variables that can be used to parameterize the KPI */
  variables?: KPIVariable[];

  /** Recommended actions and tools for this KPI */
  recommendedActions?: KPIRecommendation[];
}

/**
 * KPI references to schema fields
 */
export interface SchemaReferences {
  tables: string[]; // Tables used in formula (e.g., 'activityFeed', 'itemSet')
  fields: {
    // Fields referenced in formula
    [tableName: string]: string[]; // Table name -> field names
  };
  joins?: {
    // Required joins
    from: string; // From table
    to: string; // To table
    on: string[]; // Join conditions
  }[];
}

/**
 * Enhanced benchmark configuration for KPIs
 */
export interface EnhancedBenchmark {
  /** Standard benchmarks at different organizational levels */
  standards: {
    /** Facility-level benchmarks */
    site?: BenchmarkStandard;
    /** Company or regional benchmarks */
    organization?: BenchmarkStandard;
    /** Industry standard benchmarks */
    industry?: BenchmarkStandard;
    /** SCOR model reference points */
    scor?: BenchmarkStandard;
    /** Digital twin derived benchmarks */
    digitalTwin?: BenchmarkStandard;
    /** Regulatory requirements */
    regulatory?: BenchmarkStandard;
  };
  /** Time windows for trend analysis */
  timeWindows: TimeWindowConfig[];
  /** Primary source of the benchmark data */
  primarySource:
    | 'site'
    | 'organization'
    | 'industry'
    | 'scor'
    | 'digitalTwin'
    | 'regulatory';
  /** Metadata about benchmark collection */
  metadata?: {
    /** When benchmark was last updated */
    lastUpdated?: string;
    /** Data source details */
    source?: string;
    /** Sample size if calculated from data */
    sampleSize?: number;
    /** References to supporting documentation */
    references?: string[];
  };
}

/**
 * Standard benchmark values for a specific level
 */
export interface BenchmarkStandard {
  /** Target/ideal value */
  target: number;
  /** Acceptable range */
  acceptable?: {
    min: number;
    max: number;
  };
  /** Warning thresholds */
  warning?: {
    lower?: number;
    upper?: number;
  };
  /** Critical thresholds */
  critical?: {
    lower?: number;
    upper?: number;
  };
  /** Date when this standard was established */
  effectiveDate?: string;
  /** Statistical basis (median, 90th percentile, etc.) */
  basis?: string;
}

/** Configure one time-window (instant, rolling, cumulative) */
export interface TimeWindowConfig {
  /** Unique name, e.g. "sameShift","7DayRolling","monthly" */
  name: string;
  /** ISO-8601 duration or days of lookback (0 = current only) */
  duration: string | number;
  /** Threshold type for this window */
  thresholdType: 'static' | 'percentile' | 'spc' | 'scor';
  /** Labelled ranges (static or after computation) */
  ranges: ThresholdRange[];
  /** Percentile settings (only if thresholdType === "percentile") */
  percentiles?: number[]; // e.g. [0.10,0.25,0.50,0.75,0.90]
  /** SPC settings (only if thresholdType === "spc") */
  spc?: SPCConfig;
  /** SCOR mapping (only if thresholdType === "scor") */
  scor?: SCORThresholdConfig;
}

/** A simple labelled band */
export interface ThresholdRange {
  label: string; // e.g. "Low","Average","High"
  min: number; // inclusive
  max: number; // inclusive
  color?: string; // optional color code
  textColor?: string; // text color for contrast
  actions?: string[]; // suggested actions when in this range
}

/** Statistical Process Control parameters */
export interface SPCConfig {
  /** Use mean ± n sigma */
  sigmaMultiplier: number; // e.g. 2 or 3
  /** If true, recalc control limits per window */
  rolling?: boolean;
}

/** SCOR-style threshold, pct of a target */
export interface SCORThresholdConfig {
  /** % of target lower bound (e.g. 0.9 = 90%) */
  minPct: number;
  /** % of target upper bound (e.g. 1.1 = 110%) */
  maxPct: number;
  /** Label names for colour bands */
  severity?: {
    good: string; // e.g. "Green"
    warning: string; // e.g. "Yellow"
    critical: string; // e.g. "Red"
  };
}

/**  KPI groups  */
export const KPI_GROUPS = [
  'kpi', //
  'activity', //
  'analyze', //
  'costs', //
  'warehouse_summary', // high-level health
  'today', // near-real-time control-tower
  'actual', // Actual time
  'cost', // cost
  'iot', //
  'throughput', //
  'efficiency', //
  'performance', //
  'productivity', //
  'operations', // near-real-time control-tower
  'planned', // Planned
  'digital_twin_simulation', // Planned
  'process', // per process (picking, put-away …)
  'inventory', // stock, capacity, accuracy
  'labour', // efficiency, utilisation
  'clients', // 3PL view
  'insights', // hidden inefficiencies by DT
  'issues', // congestion, mis-placement …
  'compliance', // storage / safety standards
  'projects', // CI initiatives tracking
  'heatmaps', // spatial analytics
  'data_io', // import / export hygiene
  'uom', // Units and Item Master Data
  'general',
] as const;
export type KPIGroup = (typeof KPI_GROUPS)[number];

/**
 * KPI tags for categorization and filtering
 */
export const KPI_TAGS = {
  // ISO 22400 reference categories
  ISO_STANDARDS: [
    'iso-22400:PEE', // Production Equipment Effectiveness
    'iso-22400:QAS', // Quality Allocation Status
    'iso-22400:WE', // Worker Efficiency
    'iso-22400:WU', // Worker Utilization
    'iso-22400:OEE', // Overall Equipment Effectiveness
    'iso-22400:CPE', // Critical Process Efficiency
  ],

  // Metric types
  METRIC_TYPE: [
    'efficiency',
    'productivity',
    'performance',
    'quality',
    'cost',
    'throughput',
    'utilization',
    'cycle_count',
    'time',
    'actual',
    'planned',
    'process',
    'digital_twin_simulation',
    'labor',
    'safety',
    'compliance',
  ],

  // Data source categories
  DATA_SOURCE: [
    'wms',
    'erp',
    'wcs',
    'wearable',
    'iot',
    'digital_twin_simulation',
    'manual_entry',
    'external_api',
  ],

  // Planning horizon categories
  TIME_SCOPE: [
    'real_time',
    'daily',
    'weekly',
    'monthly',
    'quarterly',
    'annual',
  ],
} as const;

/**
 * Alert severity levels
 */
export enum AlertSeverity {
  INFO = 'info',
  WARNING = 'warning',
  CRITICAL = 'critical',
}

/**
 * Interface for KPI threshold configuration
 */
export interface KPIThresholdConfig {
  kpiId: string;
  thresholds: {
    warning: {
      lower?: number;
      upper?: number;
    };
    critical: {
      lower?: number;
      upper?: number;
    };
  };
}

/**
 * Interface for KPI visualization settings
 */
export interface KPIVisualizationSettings {
  kpiId: string;
  chartType: 'line' | 'bar' | 'gauge' | 'table' | 'kpi' | 'heatmap';
  colorScheme?: string;
  showTarget?: boolean;
  showThresholds?: boolean;
  aggregation?: 'sum' | 'avg' | 'min' | 'max' | 'count';
  timeGranularity?:
    | 'hour'
    | 'shift'
    | 'day'
    | 'week'
    | 'month'
    | 'quarter'
    | 'year';
}

/**
 * Interface for KPI-derived action
 */
export interface KPIAction {
  kpiId: string;
  condition: string; // e.g., "value < benchmark.lowerCrit"
  severity: AlertSeverity;
  actionType: 'notification' | 'alert' | 'workflow' | 'report';
  message: string;
  suggestedActions?: string[];
  autoRemediate?: boolean;
}

// New Interfaces for actionable recommendations

/**
 * Tools used for KPI-driven actions, e.g., Slotting Tool, Root Cause Analysis, Scheduler, etc.
 */
export interface KPITool {
  id: string;
  name: string;
  description: string;
  category:
    | 'rootCauseAnalysis'
    | 'Analyze'
    | 'Allocate'
    | 'Optimize'
    | 'BatchOrders'
    | 'ScheduleLabour'
    | 'ScheduleDocks'
    | 'Solver'
    | string;
  link?: string; // optional URL to the tool if available
}

/**
 * Recommendations for KPIs, including suggested actions and tools.
 * These recommendations help to make KPIs actionable decision helpers.
 */
export interface KPIRecommendation {
  kpiId: string;
  recommendedAction: string;
  tools?: KPITool[];
  steps?: string[];
  priority?: 'low' | 'medium' | 'high';
}

/**
 * Parsers for ISO-8601 duration strings used in KPI model
 */

/**
 * Parses ISO-8601 duration strings
 * Examples: P28D (28 days), P90D (90 days), P7D (7 days)
 *
 * @param isoDuration ISO-8601 duration string (e.g., "P28D", "P1YT6H")
 * @returns Duration object with parsed components
 */
export function parseIsoDuration(isoDuration: string): {
  years: number;
  months: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
} {
  // Initialize all duration components to 0
  const duration = {
    years: 0,
    months: 0,
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  };

  // Return empty duration for invalid input
  if (!isoDuration || typeof isoDuration !== 'string') {
    return duration;
  }

  // Ensure string starts with 'P' (period designator)
  if (!isoDuration.startsWith('P')) {
    console.warn('Invalid ISO duration format, must start with P', isoDuration);
    return duration;
  }

  // Check if duration contains time components
  const hasTime = isoDuration.includes('T');

  // Split into date and time parts
  const datePart = hasTime
    ? isoDuration.substring(1, isoDuration.indexOf('T'))
    : isoDuration.substring(1);
  const timePart = hasTime
    ? isoDuration.substring(isoDuration.indexOf('T') + 1)
    : '';

  // Parse date components (years, months, days)
  const yearMatch = datePart.match(/(\d+)Y/);
  const monthMatch = datePart.match(/(\d+)M/);
  const dayMatch = datePart.match(/(\d+)D/);

  if (yearMatch) duration.years = parseInt(yearMatch[1], 10);
  if (monthMatch) duration.months = parseInt(monthMatch[1], 10);
  if (dayMatch) duration.days = parseInt(dayMatch[1], 10);

  // Parse time components (hours, minutes, seconds)
  if (hasTime) {
    const hourMatch = timePart.match(/(\d+)H/);
    const minuteMatch = timePart.match(/(\d+)M/);
    const secondMatch = timePart.match(/(\d+)S/);

    if (hourMatch) duration.hours = parseInt(hourMatch[1], 10);
    if (minuteMatch) duration.minutes = parseInt(minuteMatch[1], 10);
    if (secondMatch) duration.seconds = parseInt(secondMatch[1], 10);
  }

  return duration;
}

/**
 * Converts ISO-8601 duration to milliseconds
 * Note: Uses approximate values for years and months
 *
 * @param isoDuration ISO-8601 duration string (e.g., "P28D", "P1YT6H")
 * @returns Duration in milliseconds
 */
export function isoDurationToMs(isoDuration: string): number {
  const duration = parseIsoDuration(isoDuration);

  // Approximate conversions (not exact due to leap years, varying month lengths)
  const yearMs = 365.25 * 24 * 60 * 60 * 1000;
  const monthMs = 30.44 * 24 * 60 * 60 * 1000;
  const dayMs = 24 * 60 * 60 * 1000;
  const hourMs = 60 * 60 * 1000;
  const minuteMs = 60 * 1000;
  const secondMs = 1000;

  return (
    duration.years * yearMs +
    duration.months * monthMs +
    duration.days * dayMs +
    duration.hours * hourMs +
    duration.minutes * minuteMs +
    duration.seconds * secondMs
  );
}

/**
 * Gets a human-readable description of an ISO duration
 *
 * @param isoDuration ISO-8601 duration string (e.g., "P28D", "P1YT6H")
 * @returns Humanized description (e.g., "28 days", "1 year and 6 hours")
 */
export function humanizeIsoDuration(isoDuration: string): string {
  const duration = parseIsoDuration(isoDuration);
  const parts: string[] = [];

  if (duration.years)
    parts.push(`${duration.years} ${duration.years === 1 ? 'year' : 'years'}`);
  if (duration.months)
    parts.push(
      `${duration.months} ${duration.months === 1 ? 'month' : 'months'}`,
    );
  if (duration.days)
    parts.push(`${duration.days} ${duration.days === 1 ? 'day' : 'days'}`);
  if (duration.hours)
    parts.push(`${duration.hours} ${duration.hours === 1 ? 'hour' : 'hours'}`);
  if (duration.minutes)
    parts.push(
      `${duration.minutes} ${duration.minutes === 1 ? 'minute' : 'minutes'}`,
    );
  if (duration.seconds)
    parts.push(
      `${duration.seconds} ${duration.seconds === 1 ? 'second' : 'seconds'}`,
    );

  if (parts.length === 0) return '0 seconds';
  if (parts.length === 1) return parts[0];

  const lastPart = parts.pop();
  return `${parts.join(', ')} and ${lastPart}`;
}

export const KPI_TAG_SCHEMA: MappingSchema<any> = {
  title: 'KPI Tag Metadata',
  description:
    'Translatable schema for KPI tags with title, description, and color.',
  fields: [
    {
      name: 'tag',
      type: 'string',
      title: 'Tag',
      description:
        'The unique tag identifier (e.g., iso-22400:PEE, picking, efficiency)',
    },
    {
      name: 'title',
      type: 'string',
      title: 'Title',
      description: 'Translatable display name for the tag',
    },
    {
      name: 'description',
      type: 'string',
      title: 'Description',
      description: 'Translatable description of the tag',
    },
    {
      name: 'color',
      type: 'string',
      title: 'Color',
      description: 'Color for UI display (from themeWareBeeDark)',
    },
  ],
};

// KPI_TAG_METADATA: All tag titles and descriptions are i18next translatable.
// For OPERATIONAL tags, we recommend reusing translation keys from useActivityEventProcessTypeDetails (namespace 'feed').
// For other tags, use the 'app' namespace.
export const KPI_TAG_METADATA = {
  // Metric types (METRIC_TYPE)
  throughput: {
    title: t('Throughput', { ns: 'app' }),
    description: t('KPIs related to the rate of processing goods.', {
      ns: 'app',
    }),
    color: TwTheme.extend.colors.heatmap[40],
  },
  actual: {
    title: t('Actual', { ns: 'app' }),
    description: t('KPIs related to time measurements.', { ns: 'app' }),
    color: TwTheme.extend.colors.timeline.selected,
  },

  labour: {
    title: t('Labour', { ns: 'app' }),
    description: t('KPIs related to labour and workforce.', { ns: 'app' }),
    color: TwTheme.extend.colors.eventGroups.labourTotal,
  },
  efficiency: {
    title: t('Efficiency', { ns: 'app' }),
    description: t('KPIs related to resource and process efficiency.', {
      ns: 'app',
    }),
    color: TwTheme.extend.colors.heatmap[20],
  },

  performance: {
    title: t('Performance', { ns: 'app' }),
    description: t(
      'KPIs related to performance: overall speed & accuracy (jobs/hr).',
      { ns: 'app' },
    ),
    color: TwTheme.extend.colors.heatmap[30],
  },
  productivity: {
    title: t('Productivity Standards', { ns: 'app' }),
    description: t('KPIs related to output and productivity.', { ns: 'app' }),
    color: TwTheme.extend.colors.heatmap[30],
  },
  cost: {
    title: t('Cost', { ns: 'app' }),
    description: t('KPIs related to cost and financial performance.', {
      ns: 'app',
    }),
    color: TwTheme.extend.colors.heatmap[90],
  },

  planned: {
    title: t('Planned', { ns: 'app' }),
    description: t('KPIs related to planned time, such as Shifts', {
      ns: 'app',
    }),
    color: TwTheme.extend.colors.heatmap[30],
  },
  utilisation: {
    title: t('Utilisation', { ns: 'app' }),
    description: t('KPIs related to utilisation of resources.', { ns: 'app' }),
    color: TwTheme.extend.colors.eventGroups.labourTotal,
  },

  process: {
    title: t('Process', { ns: 'app' }),
    description: t('KPIs related to process metrics and adherence.', {
      ns: 'app',
    }),
    color: TwTheme.extend.colors.eventGroups.labourTotal,
  },

  quality: {
    title: t('Quality', { ns: 'app' }),
    description: t(
      'KPIs related to quality and error rates, e.g. pick-accuracy and fulfilment errors.',
      { ns: 'app' },
    ),
    color: TwTheme.extend.colors.heatmap[50],
  },
  safety: {
    title: t('Safety', { ns: 'app' }),
    description: t(
      'KPIs related to safety incidents, near-misses and fatigue breaks.',
      { ns: 'app' },
    ),
    color: TwTheme.extend.colors.heatmap[50],
  },
  compliance: {
    title: t('Compliance', { ns: 'app' }),
    description: t(
      'KPIs related to process compliance, policy training and audit scores.',
      { ns: 'app' },
    ),
    color: TwTheme.extend.colors.heatmap[50],
  },

  // Data source categories (DATA_SOURCE)
  wms: {
    title: t('WMS', { ns: 'app' }),
    description: t('Data sourced from the warehouse management system (WMS).', {
      ns: 'app',
    }),
    color: TwTheme.extend.colors.brand.DEFAULT,
  },
  erp: {
    title: t('WMS', { ns: 'app' }),
    description: t('Data sourced from the ERP.', {
      ns: 'app',
    }),
    color: TwTheme.extend.colors.brand.DEFAULT,
  },
  iot: {
    title: t('IoT Devices', { ns: 'app' }),
    description: t(
      'Data sourced from IoT devices such as sensors and scanners.',
      { ns: 'app' },
    ),
    color: TwTheme.extend.colors.brand.DEFAULT,
  },
  wereable: {
    title: t('Wearable Devices', { ns: 'app' }),
    description: t(
      'Data sourced from Wearable devices such as sensors and scanners.',
      { ns: 'app' },
    ),
    color: TwTheme.extend.colors.brand.DEFAULT,
  },

  digital_twin_simulation: {
    title: t('Productivity', { ns: 'app' }),
    description: t('Data generated from digital twin simulations.', {
      ns: 'app',
    }),
    color: TwTheme.extend.colors.brand.DEFAULT,
  },

  manual_entry: {
    title: t('Manual Entry', { ns: 'app' }),
    description: t('Data entered manually by warehouse staff.', { ns: 'app' }),
    color: TwTheme.extend.colors.menu.DEFAULT,
  },
  external_api: {
    title: t('External API', { ns: 'app' }),
    description: t('Data sourced from external APIs.', { ns: 'app' }),
    color: TwTheme.extend.colors.brand.DEFAULT,
  },

  // Planning horizon categories (TIME_SCOPE)
  real_time: {
    title: t('Real Time', { ns: 'app' }),
    description: t('KPIs measured in real time.', { ns: 'app' }),
    color: TwTheme.extend.colors.timeline.selected,
  },

  daily: {
    title: t('Daily', { ns: 'app' }),
    description: t('KPIs measured on a daily basis.', { ns: 'app' }),
    color: TwTheme.extend.colors.timeline.selected,
  },
  weekly: {
    title: t('Weekly', { ns: 'app' }),
    description: t('KPIs measured on a weekly basis.', { ns: 'app' }),
    color: TwTheme.extend.colors.timeline.selected,
  },
  monthly: {
    title: t('Monthly', { ns: 'app' }),
    description: t('KPIs measured on a monthly basis.', { ns: 'app' }),
    color: TwTheme.extend.colors.timeline.selected,
  },
  quarterly: {
    title: t('Quarterly', { ns: 'app' }),
    description: t('KPIs measured on a quarterly basis.', { ns: 'app' }),
    color: TwTheme.extend.colors.timeline.selected,
  },
  annual: {
    title: t('Annual', { ns: 'app' }),
    description: t('KPIs measured on an annual basis.', { ns: 'app' }),
    color: TwTheme.extend.colors.timeline.selected,
  },

  // ISO 22400 reference categories
  'iso-22400:PEE': {
    title: t('Equipment Effectiveness', { ns: 'app' }),
    description: t(
      'Measures the effectiveness of production equipment as per ISO 22400.',
      { ns: 'app' },
    ),
    color: TwTheme.extend.colors.brand.accent,
  },
  'iso-22400:QAS': {
    title: t('Quality Allocation Status', { ns: 'app' }),
    description: t(
      'Indicates the allocation status for quality as per ISO 22400.',
      { ns: 'app' },
    ),
    color: TwTheme.extend.colors.heatmap[50],
  },
  'iso-22400:WE': {
    title: t('Worker Efficiency', { ns: 'app' }),
    description: t('Measures the efficiency of workers as per ISO 22400.', {
      ns: 'app',
    }),
    color: TwTheme.extend.colors.eventGroups.handling,
  },
  'iso-22400:WU': {
    title: t('Worker Utilization', { ns: 'app' }),
    description: t('Indicates the utilization of workers as per ISO 22400.', {
      ns: 'app',
    }),
    color: TwTheme.extend.colors.eventGroups.labourTotal,
  },
  'iso-22400:OEE': {
    title: t('Overall Equipment Effectiveness', { ns: 'app' }),
    description: t(
      'Measures the overall effectiveness of equipment as per ISO 22400.',
      { ns: 'app' },
    ),
    color: TwTheme.extend.colors.heatmap[20],
  },
  'iso-22400:CPE': {
    title: t('Critical Process Efficiency', { ns: 'app' }),
    description: t(
      'Measures the efficiency of critical processes as per ISO 22400.',
      { ns: 'app' },
    ),
    color: TwTheme.extend.colors.heatmap[90],
  },
};

/**
 * Relationship between KPIs modelling causal or correlational connections
 */
export interface KPIRelationship {
  /** Related KPI ID */
  relatedKpiId: string;

  /** Type of relationship */
  relationshipType:
    | 'causes' // This KPI directly affects the related KPI
    | 'influenced_by' // This KPI is influenced by the related KPI
    | 'correlated' // These KPIs are correlated but causation is unclear
    | 'cascade' // These KPIs form a cascade (e.g., Orders -> Jobs -> Lines)
    | 'opposing' // Optimizing one may harm the other (trade-off)
    | 'supporting'; // Improving one helps the other

  /** Strength of relationship (0-1) */
  strength?: number;

  /** Delay or lag between changes in one KPI affecting the other */
  lag?: string; // ISO duration

  /** Description of the relationship */
  description?: string;

  /** Mathematical model of relationship if known */
  model?: string;

  /** Examples of how this relationship manifests */
  examples?: string[];
}

/**
 * Chart types
 */
export type ChartType =
  | 'pie'
  | 'line'
  | 'bar'
  | 'groupped-bar'
  | 'stacked-bar'
  | 'stacked-bar-percent' // For velocity distribution where we want to show the percentage of the total (horizontal bar)
  | 'gauge' // Half ring gauge
  | 'table'
  | 'kpi'
  | 'heatmap'
  // | 'radar'
  // | 'scatter'
  // | 'bubble'
  // | 'sankey'
  | 'rings' // Applue watch style rings, to show multi dimension data
  // | 'multiaxis'
  | 'stat';

export type AggregationType =
  | 'sum'
  | 'avg'
  | 'min'
  | 'max'
  | 'count'
  | 'median'
  | 'percentile';

/**
 * Recommended visualization for a KPI
 */
export interface RecommendedVisualization {
  /** Chart type */
  chartType?: ChartType[];

  /** Primary visualization type */
  primaryType?: ChartType;

  /** Secondary visualization options */
  alternativeTypes?: ChartType[];

  /** Default time range to display */
  defaultTimeRange?: string; // ISO duration

  /** Recommended aggregation method */
  aggregation?: AggregationType;

  /** Percentile to use if aggregation is 'percentile' */
  percentile?: number;

  /** Recommended color scheme */
  colorScheme?: string;

  /** Whether to show the target line/value */
  showTarget?: boolean;

  /** Whether to show threshold bands */
  showThresholds?: boolean;

  /** Whether to show forecasted values */
  showForecast?: boolean;

  /** Best dimensions to group by for this KPI */
  recommendedDimensions?: string[];

  /** Annotate chart with specific events or markers */
  annotateWith?: {
    /** Event types to show as markers */
    eventTypes?: string[];
    /** Whether to show weekends/holidays */
    timeMarkers?: boolean;
    /** Whether to show benchmarks from competitors */
    benchmarks?: boolean;
  };

  /**
   * Optional sequential palette ID for color mapping (e.g., 'elevation-sort')
   * If not provided, the default palette will be used.
   */
  sequentialPaletteId?: string;

  /**
   * Optional palette ID for sequential color mapping (e.g., 'elevation-sort')
   */
  palette?: SequentialPaletteId | QualitativePaletteId;
}

/**
 * Variable that can be used to parameterize a KPI
 */
export interface KPIVariable {
  /** Variable name (used in formula) */
  name: string;

  /** Human readable label */
  label: string;

  /** Description of what this variable controls */
  description?: string;

  /** Data type of the variable */
  type: 'string' | 'number' | 'boolean' | 'date' | 'enum' | 'dimension';

  /** Default value */
  defaultValue: any;

  /** Possible values for enum type */
  enumValues?: string[];

  /** For dimension type, what dimension this refers to */
  dimension?: string;

  /** Whether this affects the benchmark calculations */
  affectsBenchmark?: boolean;

  /** Whether this variable can be exposed to end users */
  userConfigurable?: boolean;

  /** UI component to use for this variable */
  uiComponent?:
    | 'slider'
    | 'dropdown'
    | 'checkbox'
    | 'datepicker'
    | 'radio'
    | 'text';

  /** For number type, the min and max values */
  range?: {
    min: number;
    max: number;
    step?: number;
  };
}

/**
 * Extended relationship examples for common warehouse scenarios
 */
export const COMMON_KPI_RELATIONSHIPS = {
  // Picking and Replenishment relationship
  PICKING_REPLENISHMENT: {
    description:
      'Replenishment efficiency directly impacts picking productivity by ensuring pick locations are properly stocked',
    examples: [
      'Late replenishments cause pickers to wait or skip picks, reducing productivity',
      'Batch replenishments just-in-time improve picking efficiency',
      'Proactive replenishment based on forecasted demand reduces stockouts and improves pick rates',
    ],
    model: 'PickRate = BasePickRate * ReplenishmentServiceLevel',
  },

  // Order to Job to Line relationships
  ORDER_JOB_LINE_CASCADE: {
    description:
      'Orders cascade to jobs/pick lists which cascade to pick lines, creating a hierarchical relationship',
    examples: [
      'High order count with few lines per order increases job creation overhead',
      'Consolidated orders with many lines per job increase picking density and efficiency',
      'Order batching affects job size which affects total travel distance',
    ],
    model:
      'TotalLabor = OrderSetupTime * OrderCount + JobSetupTime * JobCount + LinePickTime * LineCount',
  },

  // Item profile effects on picking
  ITEM_PROFILE_PICKING: {
    description:
      'Physical attributes of items (weight, volume, dimension) affect picking speeds',
    examples: [
      'Bulky items reduce pick rates due to handling time',
      'Heavy items slow down pickers due to ergonomic considerations',
      'Items requiring special handling (fragile, hazardous) add verification steps',
    ],
    model:
      'AdjustedPickRate = BasePickRate * (1 - WeightPenalty) * (1 - VolumePenalty) * (1 - HandlingComplexityPenalty)',
  },

  // Ordering behavior effects
  ORDERING_BEHAVIOR_PROCESS: {
    description: 'Customer ordering patterns impact warehouse processes',
    examples: [
      'Non-pallet-quantity orders require split pallet handling',
      'Inconsistent order timing creates labor spikes',
      'Last-minute orders disrupt planned picking waves',
      'Incomplete case quantities force case-breaking activities',
    ],
    model: 'ProcessEfficiency = BaseEfficiency * OrderingBehaviorMultiplier',
  },
};

/**
 * Example dynamically parameterized KPI
 */
export const EXAMPLE_PARAMETERIZED_KPI: WarehouseKPI = {
  id: 'netLinesPerformance',
  category: 'labour',
  title: 'Picking Productivity',
  description:
    'Lines picked per labor hour, adjustable by process type and time window',
  unit: 'lines/hr',
  color: [
    TwTheme.extend.colors.heatmap[30],
    getBestContrastColor(TwTheme.extend.colors.heatmap[30]),
  ],

  query:
    'SELECT COUNT(*) as pick_lines, SUM(duration) as labor_hours FROM activity_feed WHERE process_type = $processType AND timestamp >= NOW() - $timeWindow::interval',
  formula:
    'SUM(pick_lines) / SUM(labor_hours) WHERE process_type = $processType AND time_window = $timeWindow',
  getFormatted: (v: number) => ({
    value: v.toFixed(2),
    fullString: v.toFixed(2),
    unit: 'lines/hr',
    raw: v,
    unitCode: 'lines/hr',
  }),
  isPrimary: true,

  variables: [
    {
      name: 'processType',
      label: 'Process Type',
      description: 'Filter by specific picking method',
      type: 'enum',
      defaultValue: 'all',
      enumValues: [
        'all',
        'case_pick',
        'piece_pick',
        'pallet_pick',
        'batch_pick',
      ],
      userConfigurable: true,
      uiComponent: 'dropdown',
    },
    {
      name: 'timeWindow',
      label: 'Time Window',
      description: 'Analysis period',
      type: 'enum',
      defaultValue: 'P7D',
      enumValues: ['P1D', 'P7D', 'P30D', 'P90D'],
      userConfigurable: true,
      uiComponent: 'dropdown',
      affectsBenchmark: true,
    },
    {
      name: 'includeTravel',
      label: 'Include Travel Time',
      description: 'Whether to include travel time in productivity calculation',
      type: 'boolean',
      defaultValue: true,
      userConfigurable: true,
      uiComponent: 'checkbox',
    },
  ],

  recommendedVisualization: {
    primaryType: 'line',
    alternativeTypes: ['bar', 'gauge'],
    defaultTimeRange: 'P30D',
    aggregation: 'avg',
    showTarget: true,
    showThresholds: true,
    recommendedDimensions: ['worker', 'zone', 'shift', 'item_class'],
    annotateWith: {
      eventTypes: ['training', 'system_outage', 'layout_change'],
      timeMarkers: true,
    },
    // Use a subtle blue palette for upward trends
    sequentialPaletteId: 'trendAscending',
  },

  relationships: [
    {
      relatedKpiId: 'replenishment_productivity',
      relationshipType: 'influenced_by',
      strength: 0.8,
      lag: 'PT30M',
      description:
        'Replenishment delays impact picking productivity after approximately 30 minutes',
    },
    {
      relatedKpiId: 'order_line_density',
      relationshipType: 'influenced_by',
      strength: 0.7,
      description:
        'Higher lines per order improves picking density and productivity',
    },
    {
      relatedKpiId: 'item_cubic_volume',
      relationshipType: 'influenced_by',
      strength: 0.5,
      description:
        'Larger items reduce picking productivity due to handling requirements',
    },
  ],

  benchmark: {
    standards: {
      site: {
        target: 100,
        acceptable: { min: 90, max: 120 },
        warning: { lower: 85, upper: 125 },
        critical: { lower: 80, upper: 130 },
      },
      industry: {
        target: 95,
        acceptable: { min: 85, max: 110 },
      },
    },
    timeWindows: [
      {
        name: 'daily',
        duration: 'P1D',
        thresholdType: 'static',
        ranges: [
          { label: 'Below Target', min: 0, max: 90, color: '#ff4d4f' },
          { label: 'Target', min: 90, max: 110, color: '#52c41a' },
          { label: 'Above Target', min: 110, max: 999, color: '#1890ff' },
        ],
      },
    ],
    primarySource: 'site',
  },

  processType: ActivityEventProcessType.PICKING,
  trendWindow: 'P14D',
  forecastHorizon: 'P7D',
  drillDimensions: ['worker', 'zone', 'shift', 'item_class'],
};

export default {
  KPI_GROUPS,
  KPI_TAGS,
};
